import {
  Component,
  inject,
  signal,
  Signal,
  ChangeDetectionStrategy
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { UserService } from '../../services/user.service';
import {
  BadgeComponent,
  BreadcrumbsComponent,
  ButtonComponent,
  ColumnHeaderComponent,
  DividerComponent,
  FormFieldComponent,
  IconComponent,
  LabelComponent,
  ModalService,
  NotificationService,
  OptionComponent,
  PageHeaderComponent,
  PlaceholderComponent,
  SelectComponent,
  Table2Component
} from '@pansa/ui';
import { Router } from '@angular/router';
import { BlockUserDTO, UserDTO } from '../../models/user.model';
import { SortState } from '@app/shared/models/sort.model';
import { UserDetailsComponent } from '../user-details/user-details.component';
import { UserDetailsService } from '../../services/user-details.service';
import { GroupTypeEnum } from '@app/features/groups/models/group.model';
import { GroupColorPipe } from '@app/features/groups/pipes/group-color.pipe';
import { toSignal } from '@angular/core/rxjs-interop';
import { FilterSortService } from '@app/shared/services/filter-sort.service';
import { KeycloakInfoService } from '@app/shared/services/keycloak-info.service';

@Component({
  selector: 'rqa-user-list',
  templateUrl: './user-list.component.html',
  styleUrl: './user-list.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    PageHeaderComponent,
    BreadcrumbsComponent,
    ButtonComponent,
    IconComponent,
    Table2Component,
    ColumnHeaderComponent,
    UserDetailsComponent,
    BadgeComponent,
    GroupColorPipe,
    SelectComponent,
    OptionComponent,
    PlaceholderComponent,
    LabelComponent,
    FormFieldComponent,
    DividerComponent
  ]
})
export class UserListComponent {
  private userService = inject(UserService);
  private router = inject(Router);
  private userDetailsService = inject(UserDetailsService);
  private filterService = inject<FilterSortService<UserDTO>>(FilterSortService);
  private keycloakInfoService = inject(KeycloakInfoService);
  private modalService = inject(ModalService);
  private notificationService = inject(NotificationService);

  filteredUsers!: Signal<UserDTO[]>;
  filtersForm = new FormGroup({
    global: new FormControl(''),
    group: new FormControl('Wszystkie'),
    role: new FormControl('Wszystkie'),
    enabled: new FormControl('Wszystkie')
  });
  readonly sort = signal<SortState>({
    key: 'username',
    direction: ''
  });

  trackBy: keyof UserDTO = 'username';
  groupTypeOptions: string[] = Object.values(GroupTypeEnum);
  rolesOptions: Signal<string[]> = this.keycloakInfoService.roles;
  selectedRow: UserDTO | null = null;

  constructor() {
    const filtersSignal = toSignal(this.filtersForm.valueChanges, {
      initialValue: this.filtersForm.value
    });

    this.filteredUsers = this.filterService.connect({
      data: this.userService.usersResource.value,
      filters: filtersSignal,
      sort: this.sort
    });
  }

  onSortChange(sort: SortState) {
    this.sort.set(sort);
  }

  onAdd() {
    this.router.navigate(['/users/add']);
  }

  onEdit(username: string, event: MouseEvent) {
    event.stopPropagation();
    this.router.navigate(['/users/edit', username]);
  }

  onShowDetails(user: UserDTO) {
    this.selectedRow = user;
    this.userDetailsService.open(user);
  }

  onDelete(user: UserDTO, event: MouseEvent): void {
    event.stopPropagation();
    this.modalService.openModal({
      type: 'warning',
      destructive: true,
      title: `Czy chcesz usunąć konto użytkownika ${user.firstName} ${user.lastName}?`,
      subTitle: 'Usunięcie użytkownika jest trwałe',
      submitText: 'Tak, usuń',
      cancelText: 'Anuluj',
      onSubmit: () => this.deleteUser(user.username)
    });
  }

  onBlock(user: UserDTO, event: MouseEvent): void {
    event.stopPropagation();

    const title = user.enabled ? `Czy chce zablokować konto użytkownika ${user.firstName} ${user.lastName}?` : `Czy chce odblokować konto użytkownika ${user.firstName} ${user.lastName}?`
    const submitText = user.enabled ? 'Tak, zablokuj' : 'Tak odblokuj';
    this.modalService.openModal({
      type: 'warning',
      destructive: true,
      title: title,
      submitText: submitText,
      cancelText: 'Anuluj',
      onSubmit: () => this.blockUser(user)
    });
  }

  onSendMail(user: UserDTO, event: MouseEvent): void {
    event.stopPropagation();

    this.modalService.openModal({
      type: 'warning',
      destructive: true,
      title: 'Czy chcesz wysłać e-mail weryfikacyjny?',
      submitText: 'Tak, wyślij',
      cancelText: 'Anuluj',
      onSubmit: () => this.sendVerifyEmail(user.username)
    });
  }

  private deleteUser(username: string): void {
    this.userService.deleteUser(username).subscribe((res) => {
      this.notificationService.success({title: 'Sukces', subTitle: `Usunięto użytkownika ${username}`});
      this.userService.refreshUsers();
    });
  }

  private blockUser(user: UserDTO): void {
    const blockMesssage = user.enabled ? 'Zablokowano' : 'Odblokowano';
    const payload: BlockUserDTO = { enabled: !user.enabled, username: user.username };
    this.userService.blockUser(payload).subscribe(res => {
      this.notificationService.success({title: 'Sukces', subTitle: `${blockMesssage} użytkownika ${payload.username}`});
      this.userService.refreshUsers();
    });
  }

  private sendVerifyEmail(username: string): void {
    this.userService.sendVerifyEmail(username).subscribe((res) => {
      this.notificationService.success({title: 'Sukces', subTitle: `Wysłano email weryfikacyjny do użytkownika ${username}`});
    });
  }
}
